/** Batch "Add to tours" dialog for the Trip Items grid (a custom `dialog(ctx)`
 *  batch action).
 *
 *  The operator picks a SOURCE tenant, then multi-selects one or more of that
 *  tenant's tours. Each selected pre-trip item is attached to the picked tours
 *  **in the item's own franchise family** — item↔tour pairs across tenants are
 *  skipped. Because you cannot create a "tour twin" in another tenant, the only
 *  cross-tenant tier is `ignore_others`; when the selection may span tenants we
 *  show a single-option info note and send `cross_tenant_strategy:'ignore_others'`
 *  so the server accepts the request. The server re-checks the cross-tenant
 *  condition per (item, tour) pair and is authoritative (a 400 surfaces as a
 *  snackbar).
 */
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useState } from 'react';

import { searchTours, type TourOption } from '@/api/sync';
import { CrossTenantStrategyField } from '@/components/DataGrid/CrossTenantStrategyField';
import type { BatchActionContext } from '@/components/DataGrid/batchTypes';
import { detectCrossTenant } from '@/components/DataGrid/detectCrossTenant';
import { RemoteAutocomplete } from '@/components/RemoteAutocomplete/RemoteAutocomplete';
import { TenantPicker, type TenantPickerValue } from '@/components/TenantPicker/TenantPicker';

/** Attaching an item to a tour can never mint a tour twin in another tenant, so
 *  `ignore_others` (skip cross-tenant item↔tour pairs) is the only tier. */
const TOUR_SET_STRATEGIES = ['ignore_others'];

interface Props {
  ctx: BatchActionContext;
  /** The grid's current tenant filter (null when unfiltered). Default source
   *  operator + one input to the cross-tenant heuristic. */
  gridTenantId: number | null;
  /** Tenant id per selected item row (ids mode) — the other input to the
   *  heuristic. A missing row (selection carried across pages) contributes
   *  `null` (unknown → the server stays authoritative). */
  selectedRowTenantIds: Array<number | null>;
}

export function AddToToursDialog({ ctx, gridTenantId, selectedRowTenantIds }: Props) {
  // The operator whose tours we attach the items to (defaults to the grid filter).
  const [sourceOp, setSourceOp] = useState<TenantPickerValue | null>(
    gridTenantId != null ? { id: gridTenantId, name: '' } : null,
  );
  const [tours, setTours] = useState<TourOption[]>([]);

  const sourceOpId = sourceOp?.id ?? null;
  // The picked tours all belong to the source operator, so the value's tenant is
  // the source operator. Cross-tenant iff any selected item is outside it.
  const crossTenant =
    sourceOpId != null &&
    detectCrossTenant(sourceOpId, ctx.selection, {
      gridTenantFilter: gridTenantId,
      rows: selectedRowTenantIds.map((tenantId) => ({ tenantId })),
    });

  // `ignore_others` is the only tier, so the strategy field is purely
  // informational — no choice to force. We always send the strategy when the
  // selection may be cross-tenant so the server accepts the request.
  const canSubmit = sourceOpId != null && tours.length > 0;

  const submit = () => {
    if (tours.length === 0) return;
    const params: Record<string, unknown> = { tour_ids: tours.map((t) => t.id) };
    if (crossTenant) params.cross_tenant_strategy = 'ignore_others';
    void ctx.runStandard(params);
    ctx.close();
  };

  return (
    <Dialog open onClose={ctx.close} fullWidth maxWidth="sm">
      <DialogTitle>Add to tours</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Attach {ctx.count.toLocaleString()} item(s) to one or more tours. This runs in the
            background.
          </Typography>

          {gridTenantId == null && (
            <TenantPicker
              value={sourceOp}
              onChange={(t) => {
                setSourceOp(t);
                setTours([]);
              }}
            />
          )}

          <RemoteAutocomplete<TourOption>
            label="Tours"
            size="small"
            multiple
            fetchOnEmpty
            value={tours}
            getOptionLabel={(o) => o.name || `#${o.id}`}
            getOptionKey={(o) => o.id}
            queryFn={async (q, signal) => {
              if (sourceOpId == null) return [];
              return searchTours(sourceOpId, q, signal);
            }}
            onChange={(v) => setTours((v as TourOption[] | null) ?? [])}
          />

          {crossTenant && (
            // Single-tier (ignore_others) → informational: the option is
            // pre-selected and there is nothing else to choose. It tells the
            // operator that items outside the tours' tenant will be skipped.
            <CrossTenantStrategyField
              value="ignore_others"
              onChange={() => {}}
              allowedStrategies={TOUR_SET_STRATEGIES}
              noun="tour"
            />
          )}

          {ctx.selection.mode === 'all_matching' && (
            <Alert severity="info">
              Acting on {ctx.matchedPhrase} items.
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={ctx.close}>Cancel</Button>
        <Button variant="contained" disabled={!canSubmit} onClick={submit}>
          Add to tours
        </Button>
      </DialogActions>
    </Dialog>
  );
}
